import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Flex,
  useToast,
} from '@chakra-ui/react'
import type { ReactElement } from 'react'
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { BsTools as BsToolsIcon } from 'react-icons/bs'
import { Link } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../../ui-kit'
import { checkOperationPermission } from '../../../../utils'
import {
  allowExtensionOperations,
  getFileURL,
  isSignedURLRequired,
} from '../../../../utils/mediaLibrary'
import { ACLPermissionEnum } from '../../../constants'
import useGetFileUrlArguments from '../../../hooks/useGetFileUrlArguments'
import type { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import { MLContext, UserContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import RemoveBgModal from './RemoveBgModal'

const OtherOperations = ({
  fileDetails,
}: {
  fileDetails: FileDetailsResponse
}): ReactElement | null => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [, dispatch] = useContext(MLContext)
  const [isModalOpen, ToggleModal] = useState(false)

  const toast = useToast()

  const { clientInfo } = useContext(PublicLinksInfoContext)

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    clientInfo,
    user,
  })

  const { request } = useFetch<string>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [imageURLToUse, setImageURLToUse] = useState<string>('')

  const getAndSetFileURL = useCallback(async () => {
    try {
      const { isPrivateFile, isPublished, url } = fileDetails
      if (
        isSignedURLRequired({
          isPrivateFile,
          isPublished,
          onlySignedGet: getFileUrlDynamicArguments.onlySignedGet,
          onlyNamedGet: getFileUrlDynamicArguments.onlyNamedGet,
        }) ||
        typeof getFileUrlDynamicArguments.firstCustomCname !== 'undefined'
      ) {
        setImageURLToUse(
          await getFileURL({
            file: fileDetails,
            ...getFileUrlDynamicArguments,
            request,
            transformation: [],
          }),
        )
      } else {
        setImageURLToUse(url)
      }
    } catch (err) {
      toast({
        description: t(
          'something_went_wrong',
          'Something went wrong. Please try again.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }, [fileDetails, getFileUrlDynamicArguments])

  useEffect(() => {
    void getAndSetFileURL()
  }, [fileDetails, getFileUrlDynamicArguments])

  const accordionItems = useMemo<React.ReactElement[]>(() => {
    const items: React.ReactElement[] = []
    if (
      allowExtensionOperations(fileDetails) &&
      checkOperationPermission({
        user,
        aclPermission: [ACLPermissionEnum.CONTRIBUTE, ACLPermissionEnum.MANAGE],
        selectedFiles: [fileDetails],
        selectedFolders: [],
        selectedMediaCollections: [],
      })
    ) {
      items.push(
        <AccordionItem
          borderTopWidth={0}
          borderBottomWidth={1}
          key="remove_bg_operation"
        >
          <AccordionButton py={6}>
            <Flex
              flex="1"
              textAlign="left"
              lineHeight="1.5em"
              fontWeight="semibold"
              alignItems="center"
              style={{ gap: '1em' }}
            >
              <BsToolsIcon fontSize="1.5em" />
              {t('other_operations', 'Other Operations')}
            </Flex>
            <AccordionIcon color={colors.appSlate} display="block" />
          </AccordionButton>

          <AccordionPanel>
            <Flex gap="4" flexWrap="wrap">
              <Button
                as={Link}
                to={`/media-library?semanticUrl=${encodeURIComponent(
                  imageURLToUse,
                )}&minScore=0.8&sort=DESC_RELEVANCE`}
                onClick={() => {
                  dispatch([
                    {
                      type: MediaLibraryActions.RESET_SEARCH,
                    },
                    {
                      type: MediaLibraryActions.SET_SEARCH_SEMANTIC_VALUE,
                      payload: '',
                    },
                    {
                      type: MediaLibraryActions.SET_SEARCH_SEMANTIC_URL_VALUE,
                      payload: '',
                    },
                    {
                      type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                    },
                    {
                      type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
                      payload: false,
                    },
                    {
                      type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
                      payload: false,
                    },
                  ])
                }}
              >
                {t('find_similar', 'Find similar')}
              </Button>
              <Button
                onClick={() => {
                  ToggleModal(true)
                }}
              >
                {t('remove_background', 'Remove Background')}
              </Button>
            </Flex>
          </AccordionPanel>
        </AccordionItem>,
      )
    }

    return items
  }, [fileDetails, imageURLToUse])

  if (accordionItems.length === 0) {
    return null
  }

  return (
    <Fragment>
      {accordionItems}
      <RemoveBgModal
        isOpen={isModalOpen}
        onClose={() => {
          ToggleModal(false)
        }}
        fileDetails={fileDetails}
      />
    </Fragment>
  )
}

export default OtherOperations
